/**
 * TelemetryEditor — Phase 1.7h v1
 * Drop-in textarea replacement that captures writing telemetry for
 * discussion-board posts:
 *   - typed_chars      (keystrokes that produced visible chars)
 *   - paste_events     ({at_ms, length, snippet} per paste)
 *   - revision_count   (each backspace/delete of >5 chars)
 *   - focused_seconds  (seconds the textarea had focus)
 *   - idle_seconds     (focused but no input for ≥3s, accumulated)
 *
 * Plus an integrated "Check authenticity" action that calls the v1
 * backend and shows inline hallucination + originality scores next to
 * every detected reference, plus a composite authenticity score.
 *
 * Signal, not verdict — the wording everywhere reinforces this.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/apiClient';

const URL_RE = /https?:\/\/[^\s)\]\}>,;"]+/gi;
const DOI_RE = /\b10\.\d{4,9}\/[-._;()/:A-Z0-9]+/gi;

const tone = (pct, invert = false) => {
  // For "lower is better" metrics (hallucination, paste) call with invert=false
  // For "higher is better" metrics (originality, authenticity) call with invert=true
  const p = invert ? 100 - pct : pct;
  if (p <= 20) return { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300' };
  if (p <= 50) return { bg: 'bg-amber-100',   text: 'text-amber-800',   border: 'border-amber-300' };
  return            { bg: 'bg-red-100',    text: 'text-red-800',     border: 'border-red-300' };
};

const Badge = ({ label, value, suffix = '%', invert = false, title }) => {
  if (value == null) return null;
  const t = tone(value, invert);
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-mono font-semibold border ${t.bg} ${t.text} ${t.border}`}
      title={title}
    >
      <span className="font-bold">{label}</span>
      <span>{Math.round(value)}{suffix}</span>
    </span>
  );
};

const TelemetryEditor = ({
  value,
  onChange,
  placeholder,
  minHeight = 80,
  context,
  showCheckBtn = true,
  onAuthenticityResult,    // optional callback when result arrives
  onTelemetryChange,       // optional: called with the current telemetry payload
  testidPrefix = 'tele',
}) => {
  const taRef = useRef(null);
  const startedAtRef = useRef(Date.now());
  const lastInputAtRef = useRef(Date.now());
  const focusEnterRef = useRef(null);     // ms when focus entered
  const focusedTotalRef = useRef(0);      // accumulated ms focused
  const idleTotalRef = useRef(0);         // accumulated ms idle (focused + no input ≥3s)
  const idleAccumRef = useRef(0);         // current idle window ms
  const prevLenRef = useRef(value?.length || 0);
  const [typedChars, setTypedChars] = useState(0);
  const [pasteEvents, setPasteEvents] = useState([]);
  const [revisionCount, setRevisionCount] = useState(0);
  const [tick, setTick] = useState(0);    // for re-rendering the live counters
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  // Tick once a second so focused/idle counters update visibly
  useEffect(() => {
    const i = setInterval(() => {
      if (focusEnterRef.current != null) {
        const now = Date.now();
        const since = now - lastInputAtRef.current;
        if (since >= 3000) {
          idleAccumRef.current += 1000;
        } else {
          idleAccumRef.current = 0;
        }
      }
      setTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(i);
  }, []);

  const handleChange = (e) => {
    const next = e.target.value;
    const delta = next.length - prevLenRef.current;
    if (delta > 0) {
      // Net positive = typing (or paste, paste handler also fires)
      setTypedChars((c) => c + delta);
    } else if (delta < -5) {
      // Big delete = a revision pass
      setRevisionCount((c) => c + 1);
    }
    prevLenRef.current = next.length;
    lastInputAtRef.current = Date.now();
    idleAccumRef.current = 0;
    onChange(next);
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData || window.clipboardData).getData('text') || '';
    if (!pasted) return;
    setPasteEvents((evts) => [
      ...evts,
      { at_ms: Date.now() - startedAtRef.current, length: pasted.length, snippet: pasted.slice(0, 120) },
    ]);
    // typed-chars compensation: subtract the paste so typed_chars stays "keystroke-ish"
    setTypedChars((c) => Math.max(0, c));  // delta will be added by handleChange too; net effect: pasted chars are NOT counted as typed
    setTimeout(() => {
      // After paste handler completes, the textarea value will update.
      // We pre-emptively reduce typedChars by the pasted length on the next change cycle.
      setTypedChars((c) => Math.max(0, c - pasted.length));
    }, 0);
  };

  const onFocus = () => { focusEnterRef.current = Date.now(); lastInputAtRef.current = Date.now(); };
  const onBlur = () => {
    if (focusEnterRef.current != null) {
      focusedTotalRef.current += Date.now() - focusEnterRef.current;
      idleTotalRef.current += idleAccumRef.current;
      focusEnterRef.current = null;
      idleAccumRef.current = 0;
    }
  };

  const liveFocused = focusedTotalRef.current
    + (focusEnterRef.current != null ? Date.now() - focusEnterRef.current : 0);
  const liveIdle = idleTotalRef.current + idleAccumRef.current;

  const buildTelemetry = () => ({
    typed_chars: typedChars,
    final_length: value?.length || 0,
    paste_events: pasteEvents,
    revision_count: revisionCount,
    focused_seconds: Math.round(liveFocused / 1000),
    idle_seconds: Math.round(liveIdle / 1000),
    session_started_at: new Date(startedAtRef.current).toISOString(),
  });

  // Push the latest telemetry up to the parent once a second so submit
  // handlers always have current numbers (no ref-juggling required).
  useEffect(() => {
    if (onTelemetryChange) onTelemetryChange(buildTelemetry());
    /* eslint-disable-next-line */
  }, [tick, value]);

  // Pre-detect refs client-side for instant inline display before LLM check
  const detectedRefs = useMemo(() => {
    const text = value || '';
    const out = [];
    const seen = new Set();
    for (const m of text.matchAll(URL_RE)) {
      const url = m[0].replace(/[.,;:)\u201d"']+$/, '');
      if (!seen.has(url)) { seen.add(url); out.push({ kind: 'url', value: url }); }
    }
    for (const m of text.matchAll(DOI_RE)) {
      if (!seen.has(m[0])) { seen.add(m[0]); out.push({ kind: 'doi', value: m[0] }); }
    }
    return out;
  }, [value]);

  const refScoreMap = useMemo(() => {
    const map = {};
    if (result?.references) {
      for (const r of result.references) map[r.value] = r;
    }
    return map;
  }, [result]);

  const runCheck = async () => {
    if (!value || value.trim().length < 20) {
      setErr('Write at least a couple of sentences before checking.');
      return;
    }
    setChecking(true); setErr('');
    try {
      const { data } = await api.post('/api/discussions/check-authenticity', {
        text: value,
        telemetry: buildTelemetry(),
        context: context || null,
      });
      setResult(data);
      onAuthenticityResult && onAuthenticityResult(data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Authenticity check failed');
    } finally { setChecking(false); }
  };

  const composite = result?.authenticity_score;
  const compositeTone = composite != null ? tone(composite, true) : null;

  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-editor`}>
      <textarea
        ref={taRef}
        className="input-field font-mono"
        style={{ minHeight }}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onPaste={handlePaste}
        onFocus={onFocus}
        onBlur={onBlur}
        maxLength={20000}
        data-testid={`${testidPrefix}-textarea`}
      />

      {/* Live telemetry strip */}
      <div className="flex items-center gap-3 text-[11px] text-gray-500 flex-wrap" data-testid={`${testidPrefix}-strip`}>
        <span>📝 typed {typedChars}</span>
        <span>📋 pastes {pasteEvents.length}{pasteEvents.reduce((s, p) => s + p.length, 0) > 0
              ? ` (${pasteEvents.reduce((s, p) => s + p.length, 0)} chars)`
              : ''}</span>
        <span>↩ revisions {revisionCount}</span>
        <span>⏱ focused {Math.round(liveFocused / 1000)}s</span>
        {liveIdle > 0 && <span>💤 idle {Math.round(liveIdle / 1000)}s</span>}
        {detectedRefs.length > 0 && (
          <span className="ml-auto">🔗 {detectedRefs.length} reference{detectedRefs.length === 1 ? '' : 's'} detected</span>
        )}
      </div>

      {/* Action row */}
      {showCheckBtn && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button
            type="button"
            onClick={runCheck}
            disabled={checking || !value || value.trim().length < 20}
            className="btn-outline text-xs"
            data-testid={`${testidPrefix}-check-btn`}
            title="Run a coaching authenticity check on your draft. Signal, not verdict."
          >
            {checking ? '⏳ Claude analyzing…' : '✨ Check authenticity'}
          </button>
          {result && composite != null && (
            <span
              className={`inline-flex items-center gap-2 px-2 py-1 rounded border ${compositeTone.bg} ${compositeTone.text} ${compositeTone.border}`}
              data-testid={`${testidPrefix}-composite`}
            >
              <span className="text-[10px] uppercase tracking-wider font-bold">Authenticity</span>
              <span className="font-mono font-bold text-base">{composite}</span>
            </span>
          )}
        </div>
      )}

      {err && <div className="p-2 rounded border border-red-200 bg-red-50 text-red-700 text-xs">{err}</div>}

      {/* Result panel */}
      {result && (
        <div className="card p-3 text-xs space-y-3" data-testid={`${testidPrefix}-result`}>
          <div className="flex flex-wrap gap-2">
            <Badge label="Paste"   value={result.signals.paste_ratio_pct}     invert={false}
                   title="Percent of your post that came from clipboard. Lower is more authentic." />
            <Badge label="Revisions"  value={result.signals.revision_richness}  invert={true}
                   title="Edits + active time signal. Higher = more iterative writing." />
            {result.signals.ai_text_suspicion_pct != null && (
              <Badge label="AI-text" value={result.signals.ai_text_suspicion_pct} invert={false}
                     title={result.signals.ai_text_reason || 'Claude\u2019s subjective probability this was written by an LLM. 50 = unsure.'} />
            )}
            {result.signals.elaboration_score != null && (
              <Badge label="Elaboration" value={result.signals.elaboration_score} invert={true}
                     title={result.signals.elaboration_reason || 'How much original thought beyond sources.'} />
            )}
          </div>

          {/* Reasoning snippets */}
          {(result.signals.ai_text_reason || result.signals.elaboration_reason) && (
            <div className="space-y-1 text-gray-600 italic">
              {result.signals.ai_text_reason && (
                <div><span className="font-bold not-italic text-gray-700">AI-text:</span> {result.signals.ai_text_reason}</div>
              )}
              {result.signals.elaboration_reason && (
                <div><span className="font-bold not-italic text-gray-700">Elaboration:</span> {result.signals.elaboration_reason}</div>
              )}
            </div>
          )}

          {/* Per-reference */}
          {result.references && result.references.length > 0 && (
            <div>
              <div className="font-bold text-gray-700 mb-1">References ({result.references.length})</div>
              <ul className="space-y-1.5">
                {result.references.map((r, i) => (
                  <li key={i} className="flex items-start gap-2" data-testid={`${testidPrefix}-ref-${i}`}>
                    <Badge label="H" value={r.hallucination_pct} invert={false}
                           title={r.hallucination_reason} />
                    <Badge label="O" value={r.originality_pct} invert={true}
                           title={r.originality_reason} />
                    <a href={r.value} target="_blank" rel="noopener noreferrer"
                       className="text-blue-700 hover:underline break-all">{r.value}</a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="text-[10px] text-gray-400 italic pt-1 border-t">
            Signal, not verdict. Use these scores as a coaching prompt — your instructor reviews the post.
          </div>
        </div>
      )}
    </div>
  );
};

export default TelemetryEditor;
